import { useCallback, useEffect, useRef } from 'react';

const HISTORY_STATE_KEY = 'exportDrawer';

const getCurrentHistoryState = (): Record<string, unknown> => {
  const state = window.history.state;

  return state && typeof state === 'object' ? state : {};
};

export const useDrawerBackButton = (isOpen: boolean, onClose: () => void, onOpen: () => void) => {
  const historyPushedRef = useRef(false);
  const programmaticCloseInProgressRef = useRef(false);

  useEffect(() => {
    if (isOpen && !historyPushedRef.current) {
      programmaticCloseInProgressRef.current = false;
      window.history.pushState({ ...getCurrentHistoryState(), [HISTORY_STATE_KEY]: true }, '');
      historyPushedRef.current = true;
    }

    if (!isOpen && historyPushedRef.current) {
      historyPushedRef.current = false;
      if (window.history.state?.[HISTORY_STATE_KEY]) {
        programmaticCloseInProgressRef.current = true;
        window.history.back();
      }
    }
  }, [isOpen]);

  const handlePopState = useCallback(() => {
    const isDrawerState = Boolean(window.history.state?.[HISTORY_STATE_KEY]);

    if (programmaticCloseInProgressRef.current) {
      if (isDrawerState) {
        // Still on a stale drawer history entry — keep navigating back.
        window.history.back();
        return;
      }

      programmaticCloseInProgressRef.current = false;
      return;
    }

    if (isDrawerState) {
      historyPushedRef.current = true;
      onOpen();
    } else if (historyPushedRef.current) {
      historyPushedRef.current = false;
      onClose();
    }
  }, [onClose, onOpen]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);
};
